import { useEffect, Suspense, lazy, useState, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import AOS from "aos";
import NavScrollTop from './components/NavScrollTop';
import Policies from "./pages/Policies";
import Blog from "./pages/Blog";

import Resources from "./pages/Resources";

const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Service = lazy(() => import("./pages/Service"));
const Work = lazy(() => import("./pages/Work"));
const WorkDetails = lazy(() => import("./pages/Blog"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Contact = lazy(() => import("./pages/Contact"));
const Solutions = lazy(() => import("./pages/Solutions"));
const ABMPlatforms = lazy(() => import("./pages/ABMPlatforms"));
const Marketplaces = lazy(() => import("./pages/Marketplaces"));
const AcountManager = lazy(() => import("./pages/AccountManager"));
const Publishers = lazy(() => import("./pages/Publishers"));
const PartnerManagers = lazy(() => import("./pages/PartnerManagers"));
const PersonalInformation = lazy(() => import("./pages/do-not-sell-my-personal-information"));



// Loading component with logo
const LoadingScreen = () => {
  return (
    <div className="loading-screen">
      <div className="loading-content">
        <img 
          src="/services/luma logo copy.png" 
          alt="Loading" 
          className="loading-logo"
        />
      </div>
      <style>{`
        .loading-screen {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }

        .loading-content {
          text-align: center;
        }

        .loading-logo {
          width: 150px;
          height: auto;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};

// Custom hook for route loading with better timing
const useRouteLoading = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Don't show loading on first load if it's the initial render
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Show loading immediately
    setIsLoading(true);

    // Hide loading after a short delay to ensure content is rendered
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location, isFirstLoad]);

  return isLoading;
};

function App() {
  useEffect(() => {
    AOS.init({
      offset: 80,
      duration: 1000,
      once: true,
      easing: 'ease',
    });
    AOS.refresh();
  }, []);

  return (
    <Router>
      <NavScrollTop>
        <Suspense fallback={<LoadingScreen />}>
          <RoutesWithLoading />
        </Suspense>
      </NavScrollTop>
    </Router>
  );
}

// Separate component to use useLocation hook
const RoutesWithLoading = () => {
  const isLoading = useRouteLoading();
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");

  useEffect(() => {
    if (location !== displayLocation) {
      setTransitionStage("fadeOut");
    }
  }, [location, displayLocation]);

  const handleAnimationEnd = () => {
    if (transitionStage === "fadeOut") {
      setTransitionStage("fadeIn");
      setDisplayLocation(location);
    }
  };

  useEffect(() => {
    AOS.init({
      offset: 80,
      duration: 1000,
      once: true,
      easing: 'ease',
    });
    AOS.refresh();
  }, []);

  return (
    <>
      {isLoading && <LoadingScreen />}
      <div
        className={`route-transition ${transitionStage}`}
        onAnimationEnd={handleAnimationEnd}
      >

        
        <Routes>
          <Route path={`${process.env.PUBLIC_URL + "/"}`} element={<Home />} />
          <Route path={`${process.env.PUBLIC_URL + "/home"}`} element={<Home />} />
          <Route path={`${process.env.PUBLIC_URL + "/about"}`} element={<About />} />
          <Route path={`${process.env.PUBLIC_URL + "/service"}`} element={<Service />} />
          <Route path={`${process.env.PUBLIC_URL + "/work"}`} element={<Work />} />
          <Route path={`${process.env.PUBLIC_URL + "/Blog/:id"}`} element={<Blog />} />
          <Route path={`${process.env.PUBLIC_URL + "/privacy-policy/:id"}`} element={<PrivacyPolicy />} />
          <Route path={`${process.env.PUBLIC_URL + "/contact"}`} element={<Contact />} />
          <Route path={`${process.env.PUBLIC_URL + "/solutions"}`} element={<Solutions />} />
          <Route path={`${process.env.PUBLIC_URL + "/policies"}`} element={<Policies />} />
          <Route path={`${process.env.PUBLIC_URL + "/ABMPlatforms"}`} element={<ABMPlatforms />} />
          <Route path={`${process.env.PUBLIC_URL + "/Marketplaces"}`} element={<Marketplaces />} />
          <Route path={`${process.env.PUBLIC_URL + "/AccountManager"}`} element={<AcountManager />} />
          <Route path={`${process.env.PUBLIC_URL + "/Publishers"}`} element={<Publishers />} />
          <Route path={`${process.env.PUBLIC_URL + "/PartnerManagers"}`} element={<PartnerManagers />} />
          <Route path={`${process.env.PUBLIC_URL + "/do-not-sell-my-personal-information"}`} element={<PersonalInformation />} />
          <Route path={`${process.env.PUBLIC_URL + "/resources"}`} element={<Resources />} />
        </Routes>
      </div>
      <style>{`
        .route-transition {
          opacity: 1;
          transition: opacity 0.3s ease-in-out;
        }
        
        .route-transition.fadeOut {
          opacity: 0;
        }
        
        .route-transition.fadeIn {
          opacity: 1;
        }
      `}</style>
    </>
  );
};

export default App;